import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PropTypes } from "prop-types";
import React, { useCallback, useRef, useState } from "react";
import Collapse from "react-bootstrap/Collapse";
import LeadDrawerActions from "~/actions/lead_drawer_actions";
import Select from "~/components/forms/dropdowns/SelectWrapper/Select";
import { SOCIAL_SERVICE_OPTIONS } from "~/constants/dropdowns/SocialServiceOptions";
import useLeadAvatar from "~/hooks/useLeadAvatar";
import useLeadLinks from "~/hooks/useLeadLinks";

const EMPTY_LINK = { service_name: "", service_url: "" };

const LeadDrawerSocialLinks = ({ lead = null }) => {
  const [isOpen, setIsOpen] = useState(() =>
    localStorage.isSocialLinksOpen
      ? JSON.parse(localStorage.isSocialLinksOpen)
      : true,
  );
  const [showAddLink, setShowAddLink] = useState(false);
  const [newLink, setNewLink] = useState(EMPTY_LINK);
  const [editLink, setEditLink] = useState(EMPTY_LINK);
  const [linkErrors, setLinkErrors] = useState({});

  const avatarFileRef = useRef(null);

  const {
    links,
    loading,
    createLink,
    updateLink,
    deleteLink,
    lookupFullContact,
    showEditLink,
    resetLinks,
  } = useLeadLinks(lead.id, lead.web_links);

  const {
    updateAvatar, removeAvatar, uploading, removing,
  } = useLeadAvatar(lead.id);

  const avatarBusy = uploading || removing;

  const reloadLead = useCallback(() => {
    LeadDrawerActions.loadLead(lead.id);
  }, [lead.id]);

  const handleCollapseClick = useCallback((e) => {
    e.preventDefault();
    setIsOpen((prev) => {
      localStorage.isSocialLinksOpen = !prev;
      return !prev;
    });
  }, []);

  const validateLink = useCallback((link) => {
    const errors = {};
    if (!link.service_name) {
      errors.service_name = true;
    }
    if (!link.service_url) {
      errors.service_url = true;
    }
    setLinkErrors(errors);
    return Object.keys(errors).length === 0;
  }, []);

  const resetForm = useCallback(() => {
    setShowAddLink(false);
    setNewLink(EMPTY_LINK);
    setEditLink(EMPTY_LINK);
    setLinkErrors({});
    resetLinks();
  }, [resetLinks]);

  const handleCreateLink = useCallback(
    async (e) => {
      e.preventDefault();
      if (!validateLink(newLink)) {
        return;
      }

      setLinkErrors({});
      const success = await createLink(newLink);
      if (success) {
        setShowAddLink(false);
        setNewLink(EMPTY_LINK);
      }
    },
    [newLink, validateLink, createLink],
  );

  const handleUpdateLink = useCallback(
    async (e) => {
      e.preventDefault();
      if (!validateLink(editLink)) {
        return;
      }

      setLinkErrors({});
      const success = await updateLink(editLink);
      if (success) {
        setEditLink(EMPTY_LINK);
      }
    },
    [editLink, validateLink, updateLink],
  );

  const handleDeleteLink = useCallback(
    (link) => {
      deleteLink(link.id);
    },
    [deleteLink],
  );

  const handleEditClick = useCallback(
    (link) => {
      showEditLink(link.id);
      setEditLink({
        id: link.id,
        service_name: link.service_name,
        service_url: link.service_url,
      });
    },
    [showEditLink],
  );

  const handleLookup = useCallback(async () => {
    const success = await lookupFullContact();
    if (success) {
      reloadLead();
    }
  }, [lookupFullContact, reloadLead]);

  const handleAddAvatar = useCallback(
    async (e) => {
      if (avatarBusy) {
        return;
      }

      const file = e.target.files[0];
      if (!file) {
        return;
      }

      const success = await updateAvatar(file);
      if (success) {
        reloadLead();
      }
      e.target.value = null;
    },
    [avatarBusy, updateAvatar, reloadLead],
  );

  const handleRemoveAvatar = useCallback(async () => {
    if (avatarBusy) {
      return;
    }

    const success = await removeAvatar();
    if (success) {
      reloadLead();
    }
  }, [avatarBusy, removeAvatar, reloadLead]);

  const handleServiceNameChange = useCallback((selectedOption, setter) => {
    setter((prev) => ({
      ...prev,
      service_name: selectedOption?.value ?? "",
    }));
  }, []);

  const handleServiceUrlChange = useCallback((e, setter) => {
    setter((prev) => ({
      ...prev,
      service_url: e.target.value,
    }));
  }, []);

  const renderLinkForm = (link, setter, onSubmit) => (
    <form
      key={link.id || "new"}
      className="row no-gutters align-items-center mb-3"
      onSubmit={onSubmit}
    >
      <div className="col-4 pr-1">
        <Select
          id="service-name"
          name="service_name"
          className="social-dropdown"
          placeholder="Link Type"
          value={link.service_name}
          options={SOCIAL_SERVICE_OPTIONS}
          onChange={(option) => handleServiceNameChange(option, setter)}
          isSearchable
          isInvalid={!!linkErrors.service_name}
        />
      </div>
      <div className="col pr-1">
        <input
          type="url"
          className={`form-control form-control-sm w-100${linkErrors.service_url ? " is-invalid" : ""}`}
          name="service_url"
          value={link.service_url}
          onChange={(e) => handleServiceUrlChange(e, setter)}
        />
      </div>
      <div className="col-auto">
        <button type="submit" className="btn btn-secondary btn-sm">
          <FontAwesomeIcon icon="far fa-check-circle" />
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm px-2"
          onClick={resetForm}
        >
          <FontAwesomeIcon icon="fas fa-times" />
        </button>
      </div>
    </form>
  );

  const renderLinks = () => {
    if (!links) {
      return null;
    }

    const addLinkForm = showAddLink ? (
      <div className="col-12 p-0 mb-3">
        {renderLinkForm(newLink, setNewLink, handleCreateLink)}
      </div>
    ) : null;

    if (links.length > 0) {
      return (
        <div className="row p-0 m-0">
          {addLinkForm}
          {links.map((link) => (
            <React.Fragment key={link.id}>
              {!link.showEdit && (
                <>
                  <div className="col-10 m-0 p-0 mb-3">
                    <div className="row py-1">
                      <div className="col-3 m-0 p-0">
                        {link.service_name
                          .replace(/-/g, " ")
                          .replace(/\b\w/g, (c) => c.toUpperCase())}
                        :
                      </div>
                      <div className="col-9 m-0 p-0 text-sm text-truncate my-auto">
                        <a
                          className="text-black"
                          target="_blank"
                          href={link.service_url}
                          rel="noopener noreferrer"
                        >
                          {link.service_url}
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="col-2 m-0 p-0 mb-3 d-flex align-items-center justify-content-end">
                    <div className="pull-right">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleEditClick(link)}
                      >
                        <FontAwesomeIcon icon={["far", "fa-edit"]} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleDeleteLink(link)}
                      >
                        <FontAwesomeIcon icon={["far", "fa-trash-alt"]} />
                      </button>
                    </div>
                  </div>
                </>
              )}
              {link.showEdit &&
                renderLinkForm(editLink, setEditLink, handleUpdateLink)}
            </React.Fragment>
          ))}
        </div>
      );
    }

    return (
      <div className="row p-0 m-0">
        {addLinkForm}
        <p>No social profiles found</p>
      </div>
    );
  };

  const renderActionButtons = () => {
    if (Rails.abilities.manageLead === false) {
      return null;
    }

    if (loading) {
      return (
        <button
          type="button"
          className="btn btn-secondary btn-sm disabled"
          onClick={handleLookup}
        >
          <FontAwesomeIcon icon="far fa-spinner" pulse className="mr5" />
          Lookup Social Profiles
        </button>
      );
    }

    return (
      <div>
        <button
          type="button"
          className="btn btn-secondary btn-sm mr-1"
          onClick={() => setShowAddLink(true)}
        >
          <FontAwesomeIcon icon={["fas", "fa-plus"]} className="mr5" />
          Links
        </button>
        {!lead.photo_url && (
          <button
            type="button"
            className={`btn btn-secondary btn-sm mr-1${uploading ? " disabled" : ""}`}
            disabled={avatarBusy}
            onClick={() => avatarFileRef.current.click()}
          >
            {uploading ? (
              <FontAwesomeIcon icon="far fa-spinner" pulse className="mr5" />
            ) : (
              <FontAwesomeIcon icon={["fas", "fa-plus"]} className="mr5" />
            )}
            {uploading ? "Uploading..." : "Headshot"}
          </button>
        )}
        <input
          type="file"
          ref={avatarFileRef}
          onChange={handleAddAvatar}
          className="d-none"
        />
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={handleLookup}
        >
          <FontAwesomeIcon icon={["fas", "fa-users"]} className="mr5" />
          Lookup Social Profiles
        </button>
      </div>
    );
  };

  return (
    <div>
      <div className="pull-right">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={handleCollapseClick}
        >
          {isOpen ? (
            <FontAwesomeIcon icon={["far", "fa-minus"]} />
          ) : (
            <FontAwesomeIcon icon={["far", "fa-plus"]} />
          )}
        </button>
      </div>

      <h4 className="mb15">Social and Web</h4>

      <Collapse in={isOpen} mountOnEnter unmountOnExit>
        <div>
          {lead.photo_url && (
            <div className="row p-0">
              <div className="col-4 p-0 mr-1">
                <img
                  src={lead.photo_url}
                  alt="Lead avatar"
                  width="100%"
                  className="rounded"
                />
              </div>
              <div className="col-2 d-flex p-0 flex-column align-items-start">
                <button
                  type="button"
                  className={`btn btn-secondary btn-sm${uploading ? " disabled" : ""}`}
                  disabled={avatarBusy}
                  onClick={() => avatarFileRef.current.click()}
                >
                  {uploading ? (
                    <FontAwesomeIcon icon="far fa-spinner" pulse />
                  ) : (
                    <FontAwesomeIcon icon={["far", "fa-edit"]} />
                  )}
                </button>
                <button
                  type="button"
                  className={`btn btn-secondary btn-sm mt-1${removing ? " disabled" : ""}`}
                  disabled={avatarBusy}
                  onClick={handleRemoveAvatar}
                >
                  {removing ? (
                    <FontAwesomeIcon icon="far fa-spinner" pulse />
                  ) : (
                    <FontAwesomeIcon icon={["far", "fa-trash-alt"]} />
                  )}
                </button>
              </div>
            </div>
          )}
          <div className="mt10">{renderActionButtons()}</div>
          <div className="clearfix" />
          <div className="mt-3 mb-4">{renderLinks()}</div>
        </div>
      </Collapse>
    </div>
  );
};

LeadDrawerSocialLinks.propTypes = {
  lead: PropTypes.shape({
    id: PropTypes.number,
    web_links: PropTypes.arrayOf(PropTypes.shape({})),
    photo_url: PropTypes.string,
  }),
};

export default LeadDrawerSocialLinks;
